import cuid from "cuid";
import { useCallback, useContext, useState } from "react";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { StoreContext } from "shared/store";
import { resolveDateValuesForApi } from "./config";
import {
    ADD_SAVED_FILTER,
    DELETE_SAVED_FILTER,
    SAVED_FILTERS,
} from "./graphql";

const useAdvancedFilters = ({ filterType = "saved_filter" } = {}) => {
  const { user } = useContext(StoreContext);

  const [panelFilters, setPanelFilters] = useState([{}]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [activePresetId, setActivePresetId] = useState(null);
  const [presets, setPresets] = useState([]);

  useQuery(SAVED_FILTERS, {
    variables: {
      where: {
        type: filterType,
        user: { id: user?.id },
      },
    },
    skip: !user?.id,
    fetchPolicy: "network-only",
    onCompleted: ({ referentials }) => {
      const data = referentials?.data || [];
      setPresets(
        data.map((r) => ({
          id: r.id,
          name: r.name,
          filters: r.entries?.filters || [],
        })),
      );
    },
  });

  const [addSavedFilter] = useMutation(ADD_SAVED_FILTER);
  const [deleteSavedFilter] = useMutation(DELETE_SAVED_FILTER);

  const toGraphQLFilters = useCallback((filters) => {
    return filters.map((f) => {
      const { value, value2 } = resolveDateValuesForApi(f);
      return {
        field: f.fieldKey,
        operator: f.operator,
        value: value ?? null,
        value2: value2 ?? null,
        values: f.values ?? null,
      };
    });
  }, []);

  const applyFilters = useCallback(
    (filters) => {
      setAppliedFilters(filters);
      setActivePresetId(null);
    },
    [],
  );

  const removeFilter = useCallback(
    (index) => {
      setAppliedFilters((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        return updated;
      });
      setActivePresetId(null);
    },
    [],
  );

  const savePreset = useCallback(
    async (name, filters) => {
      const id = cuid();
      try {
        await addSavedFilter({
          variables: {
            data: {
              id,
              name,
              type: filterType,
              entries: { filters },
              user: { id: user?.id },
              ownedBy: { id: user?.organization?.id },
            },
          },
        });
        const newPreset = { id, name, filters };
        setPresets((prev) => [...prev, newPreset]);
        setAppliedFilters(filters);
        setActivePresetId(id);
      } catch (err) {
        console.error("Failed to save filter preset:", err);
      }
    },
    [addSavedFilter, user, filterType],
  );

  const applyPreset = useCallback(
    (presetId) => {
      if (activePresetId === presetId) {
        setAppliedFilters([]);
        setActivePresetId(null);
        return;
      }
      const preset = presets.find((p) => p.id === presetId);
      if (preset) {
        setAppliedFilters(preset.filters);
        setActivePresetId(presetId);
      }
    },
    [presets, activePresetId],
  );

  const deletePreset = useCallback(
    async (presetId) => {
      try {
        await deleteSavedFilter({
          variables: { where: { id: presetId } },
        });
        setPresets((prev) => prev.filter((p) => p.id !== presetId));
        if (activePresetId === presetId) {
          setAppliedFilters([]);
          setActivePresetId(null);
        }
      } catch (err) {
        console.error("Failed to delete filter preset:", err);
      }
    },
    [deleteSavedFilter, activePresetId],
  );

  const advancedFiltersVariable =
    appliedFilters.length > 0 ? toGraphQLFilters(appliedFilters) : undefined;

  return {
    panelFilters,
    setPanelFilters,
    appliedFilters,
    applyFilters,
    removeFilter,
    presets,
    activePresetId,
    savePreset,
    applyPreset,
    deletePreset,
    advancedFiltersVariable,
    filterCount: appliedFilters.length,
  };
};

export default useAdvancedFilters;
